// The checkpoint ENGINE of the mesh server (godev checkpointengine.v1): saved checkpoint programs and
// what each one looks like inside. Distinct from checkpoint-rpc-client.ts, which talks to the checkpoint
// COMPILER (checkpoint.v1, the "checkpoint" mailbox). The engine answers on the mesh server's own
// mailbox. FEATURE-20261002-go-runtime-for-checkpoint.
import GlobalClient from "../hermesclient/client";
import { ContentType, RpcFrameType } from "../hermesclient/proto/wsmessages";
import { nameMappingService } from "./NameMappingService";
import { asLogicalMailbox, MailboxAddress } from "../hermesclient/types";

// One saved program in the list.
export interface SavedProgramSummary {
  checkpointUid: string;
  kind: string; // "yield" | "checkpoint"
  processRunUid: string; // the process run that saved it
  resumeUid: string; // the process run that resumed it; "" while it waits
  planHash: string;
  mainModule: string;
  filesetUid: string; // "" when the plan is not in the plan store
  filesetName: string;
  revisionUid: string;
  createdAt: string; // RFC 3339, UTC
  jobUid: string; // the job the saving process ran under; "" when it ran under none
  processUid: string; // the checkpoint process (its overarching process run, where the console is); "" when it has none
  processState: string; // running | paused | lost | finished | failed; lost = its last leg is gone and it waits to be resumed
  resumeType: string; // what a resume must bring in, as a program writes the type (`yield[T]`); "" for nothing
  wakeAt: string; // a sleep: when the engine resumes it, RFC 3339 UTC; "" otherwise
  needsGrant: string; // paused for a grant: the capability (fs:<root>:<access>, http:<host>, endpoint:<name>, secret:<name>); "" otherwise
}

// Where a checkpoint process stands: what the run view of a program shows.
export interface ProgramStatus {
  found: boolean; // false also while the run a jobevent asked for has not started
  state: string; // spawned | running | paused | lost | finished | failed
  valueJson: string; // when finished
  message: string; // when failed
  processUid: string;
  checkpointUid: string; // when paused: the saved program it waits at
  needsGrant: string; // when paused for a grant: the capability
}

// Who a grant is made to: this run of the program, its job (every run), or its job's worker (every job
// the worker runs).
export type GrantScope = "run" | "job" | "worker";

// What GrantProgram did: the capability granted, to which run, job or worker, and the resume it sent.
export interface GrantProgramResult {
  capability: string;
  scope: string;
  scopeUid: string;
  jobEventUid: string;
  message: string;
}

// A value as the engine describes it. `kind` is "local" | "capture" | "temp"; a temp is a slot the
// compiler made for itself and has no name a person wrote.
export interface InspectedValue {
  name: string;
  kind: string;
  slot: number;
  type: string;
  text: string;
  json: string;
  isFun: boolean;
  module: string; // globals only
}

// Where a frame stands. offset/length index the file text and are what to highlight by; line/column are
// the compiler's and are shown, not computed with. The position is never exact today: `nearest` means
// the nearest expression at or before the frame's step, `declaration` the function's declaration.
export interface InspectedPosition {
  file: string;
  project: string;
  line: number;
  column: number;
  offset: number;
  length: number;
  nearest: boolean;
  declaration: boolean;
}

export interface InspectedFrame {
  funSlot: number;
  function: string;
  module: string;
  pc: number;
  position: InspectedPosition | null;
  variables: InspectedValue[];
}

export interface SavedProgram {
  found: boolean;
  checkpointUid: string;
  kind: string;
  processRunUid: string;
  resumeUid: string;
  planHash: string;
  filesetUid: string;
  revisionUid: string;
  compilerVersion: string;
  mainModule: string;
  processHandle: string;
  parentProcessHandle: string;
  value: InspectedValue | null;
  frames: InspectedFrame[]; // top first
  globals: InspectedValue[];
  problem: string; // found, but it could not be described: why
}

type Raw = Record<string, unknown>;

// protojson writes lowerCamelCase and OMITS zero values (false, 0, ""), so every field is read with a
// default; the snake_case fallback covers an encoder that keeps the proto field names.
function pick(raw: Raw, camel: string, snake: string): unknown {
  return raw[camel] ?? raw[snake];
}
const str = (raw: Raw, camel: string, snake = camel) => String(pick(raw, camel, snake) ?? "");
const num = (raw: Raw, camel: string, snake = camel) => Number(pick(raw, camel, snake) ?? 0);
const bool = (raw: Raw, camel: string, snake = camel) => Boolean(pick(raw, camel, snake) ?? false);
const list = (raw: Raw, camel: string, snake = camel): Raw[] => {
  const v = pick(raw, camel, snake);
  return Array.isArray(v) ? (v as Raw[]) : [];
};

function parseValue(raw: Raw): InspectedValue {
  return {
    name: str(raw, "name"),
    kind: str(raw, "kind"),
    slot: num(raw, "slot"),
    type: str(raw, "type"),
    text: str(raw, "text"),
    json: str(raw, "json"),
    isFun: bool(raw, "isFun", "is_fun"),
    module: str(raw, "module"),
  };
}

function parsePosition(raw: unknown): InspectedPosition | null {
  if (raw === null || typeof raw !== "object") return null;
  const p = raw as Raw;
  return {
    file: str(p, "file"),
    project: str(p, "project"),
    line: num(p, "line"),
    column: num(p, "column"),
    offset: num(p, "offset"),
    length: num(p, "length"),
    nearest: bool(p, "nearest"),
    declaration: bool(p, "declaration"),
  };
}

export function parseSavedProgram(raw: Raw): SavedProgram {
  const value = raw.value;
  return {
    found: bool(raw, "found"),
    checkpointUid: str(raw, "checkpointUid", "checkpoint_uid"),
    kind: str(raw, "kind"),
    processRunUid: str(raw, "processRunUid", "process_run_uid"),
    resumeUid: str(raw, "resumeUid", "resume_uid"),
    planHash: str(raw, "planHash", "plan_hash"),
    filesetUid: str(raw, "filesetUid", "fileset_uid"),
    revisionUid: str(raw, "revisionUid", "revision_uid"),
    compilerVersion: str(raw, "compilerVersion", "compiler_version"),
    mainModule: str(raw, "mainModule", "main_module"),
    processHandle: str(raw, "processHandle", "process_handle"),
    parentProcessHandle: str(raw, "parentProcessHandle", "parent_process_handle"),
    value: value !== null && typeof value === "object" ? parseValue(value as Raw) : null,
    frames: list(raw, "frames").map((f) => ({
      funSlot: num(f, "funSlot", "fun_slot"),
      function: str(f, "function"),
      module: str(f, "module"),
      pc: num(f, "pc"),
      position: parsePosition(f.position),
      variables: list(f, "variables").map(parseValue),
    })),
    globals: list(raw, "globals").map(parseValue),
    problem: str(raw, "problem"),
  };
}

export function parseSavedProgramList(raw: Raw): SavedProgramSummary[] {
  return list(raw, "snapshots").map((s) => ({
    checkpointUid: str(s, "checkpointUid", "checkpoint_uid"),
    kind: str(s, "kind"),
    processRunUid: str(s, "processRunUid", "process_run_uid"),
    resumeUid: str(s, "resumeUid", "resume_uid"),
    planHash: str(s, "planHash", "plan_hash"),
    mainModule: str(s, "mainModule", "main_module"),
    filesetUid: str(s, "filesetUid", "fileset_uid"),
    filesetName: str(s, "filesetName", "fileset_name"),
    revisionUid: str(s, "revisionUid", "revision_uid"),
    createdAt: str(s, "createdAt", "created_at"),
    jobUid: str(s, "jobUid", "job_uid"),
    processUid: str(s, "processUid", "process_uid"),
    processState: str(s, "processState", "process_state"),
    resumeType: str(s, "resumeType", "resume_type"),
    wakeAt: str(s, "wakeAt", "wake_at"),
    needsGrant: str(s, "needsGrant", "needs_grant"),
  }));
}

// One need of a job's program and what the a-priori check found: whether a grant covers it, and
// whether the job's worker can do it ("yes", "no", or "" where the mesh does not decide it).
export interface ProgramAccessNeed {
  kind: string;
  name: string;
  detail: string;
  dynamic: boolean;
  capability: string;
  granted: boolean;
  worker: string;
  workerReason: string;
}

// checkpointengine.v1.CheckProgramAccess: a job's program checked ahead of a run.
export interface ProgramAccess {
  ok: boolean;
  message: string;
  jobUid: string;
  jobName: string;
  workerLabel: string;
  enforced: boolean;
  revisionUid: string;
  needs: ProgramAccessNeed[];
  workerCan: boolean;
  blessedBy: string;
  blessedAt: string;
  blessedRevisionUid: string;
}

export function parseProgramAccess(raw: Raw): ProgramAccess {
  return {
    ok: raw["ok"] === true,
    message: str(raw, "message"),
    jobUid: str(raw, "jobUid", "job_uid"),
    jobName: str(raw, "jobName", "job_name"),
    workerLabel: str(raw, "workerLabel", "worker_label"),
    enforced: raw["enforced"] === true,
    revisionUid: str(raw, "revisionUid", "revision_uid"),
    needs: list(raw, "needs").map((n) => ({
      kind: str(n, "kind"),
      name: str(n, "name"),
      detail: str(n, "detail"),
      dynamic: n["dynamic"] === true,
      capability: str(n, "capability"),
      granted: n["granted"] === true,
      worker: str(n, "worker"),
      workerReason: str(n, "workerReason", "worker_reason"),
    })),
    workerCan: raw["workerCan"] === true || raw["worker_can"] === true,
    blessedBy: str(raw, "blessedBy", "blessed_by"),
    blessedAt: str(raw, "blessedAt", "blessed_at"),
    blessedRevisionUid: str(raw, "blessedRevisionUid", "blessed_revision_uid"),
  };
}

class CheckpointEngineRpcClient {
  private targetMailbox: MailboxAddress | null = null;

  private async getTargetMailbox(): Promise<MailboxAddress> {
    if (!this.targetMailbox) {
      this.targetMailbox = await nameMappingService.resolve(asLogicalMailbox("continuum"));
    }
    return this.targetMailbox;
  }

  private async rpcCall(endPoint: string, request: Raw): Promise<Raw> {
    const client = GlobalClient.get();
    const rpcResponse = await client.rawRpcCall({
      to: await this.getTargetMailbox(),
      endPoint,
      body: new TextEncoder().encode(JSON.stringify(request)),
      contentType: ContentType.Json,
    });
    if (rpcResponse.header?.rpcHeader?.frameType === RpcFrameType.ErrorResponse) {
      const errorInfo = rpcResponse.header?.rpcHeader?.errorInfo;
      throw new Error(errorInfo?.message || errorInfo?.errorNode?.frame?.message || `${endPoint} failed`);
    }
    if (!rpcResponse.data || rpcResponse.data.length === 0) return {};
    return JSON.parse(new TextDecoder().decode(rpcResponse.data)) as Raw;
  }

  // Saved programs, newest first.
  async listSaved(args: { waitingOnly?: boolean; processRunUid?: string; limit?: number } = {}): Promise<SavedProgramSummary[]> {
    const raw = await this.rpcCall("checkpointengine.v1.ListSnapshots", {
      waitingOnly: args.waitingOnly ?? false,
      processRunUid: args.processRunUid ?? "",
      limit: args.limit ?? 0,
    });
    return parseSavedProgramList(raw);
  }

  // The checkpoint process a jobevent's run belongs to (or a process by its uid) and where it stands.
  async programStatus(args: { jobEventUid?: string; processUid?: string }): Promise<ProgramStatus> {
    const raw = await this.rpcCall("checkpointengine.v1.ProcessStatus", { jobEventUid: args.jobEventUid ?? "", processUid: args.processUid ?? "" });
    return {
      found: raw["found"] === true,
      state: str(raw, "state"),
      valueJson: str(raw, "valueJson", "value_json"),
      message: str(raw, "message"),
      processUid: str(raw, "processUid", "process_uid"),
      checkpointUid: str(raw, "checkpointUid", "checkpoint_uid"),
      needsGrant: str(raw, "needsGrant", "needs_grant"),
    };
  }

  // Grant a paused program what it waits for, for this run, its job or its job's worker, and resume it.
  // The engine reads the capability, the run, the job and the worker from the saved program.
  async grantProgram(checkpointUid: string, scope: GrantScope, reason = ""): Promise<GrantProgramResult> {
    const raw = await this.rpcCall("checkpointengine.v1.GrantProgram", { checkpointUid, scope, reason });
    if (raw["ok"] !== true) throw new Error(str(raw, "message") || "the grant was refused");
    return {
      capability: str(raw, "capability"),
      scope: str(raw, "scope"),
      scopeUid: str(raw, "scopeUid", "scope_uid"),
      jobEventUid: str(raw, "jobEventUid", "job_event_uid"),
      message: str(raw, "message"),
    };
  }

  // A job's program checked ahead of a run: each need against the job's grants and its worker.
  async checkProgramAccess(jobUid: string): Promise<ProgramAccess> {
    return parseProgramAccess(await this.rpcCall("checkpointengine.v1.CheckProgramAccess", { jobUid }));
  }

  // Bless a job's program: the engine checks it and, when its worker can do every need the mesh
  // decides, grants the job every need. The capabilities granted now.
  async blessProgram(jobUid: string, reason = ""): Promise<string[]> {
    const raw = await this.rpcCall("checkpointengine.v1.BlessProgram", { jobUid, reason });
    if (raw["ok"] !== true) throw new Error(str(raw, "message") || "the blessing was refused");
    return Array.isArray(raw["granted"]) ? (raw["granted"] as unknown[]).map(String) : [];
  }

  // One saved program described in the program's own terms. Reading it claims nothing: the program
  // stays resumable.
  async inspect(checkpointUid: string): Promise<SavedProgram> {
    const raw = await this.rpcCall("checkpointengine.v1.InspectSnapshot", { checkpointUid });
    return parseSavedProgram(raw);
  }
}

let instance: CheckpointEngineRpcClient | null = null;

export function getCheckpointEngineRpcClient(): CheckpointEngineRpcClient {
  if (!instance) instance = new CheckpointEngineRpcClient();
  return instance;
}
